<script setup lang="ts">
import AddMockParticipant from '@/dev-tools/components/AddMockParticipant.vue';
import { cn } from '@pokerly/ui';
import { PButton } from '@pokerly/ui';
import { IconRobot, IconCircleFilled, IconX } from '@tabler/icons-vue';
import { useMockParticipants } from '@/dev-tools/index.js';
import { computed } from 'vue';
import type { MockParticipantStatus } from '../types.ts';
import { DEFAULT_ESTIMATION_OPTIONS } from '@/config/session-config.js';
import type { RequiredUserVote, UserRole } from '@pokerly/contracts/events/common';

const { participants, removeParticipant, setRole, vote, clearVote, voteAllRandom, isVotingAll } = useMockParticipants();

function getStatusColor(status: MockParticipantStatus): string {
    const color: Record<MockParticipantStatus, string> = {
        idle: 'fill-gray-400',
        connecting: 'fill-yellow-400',
        connected: 'fill-green-400',
        error: 'fill-red-400',
    };
    return color[status];
}

function handleRemove(id: string) {
    removeParticipant(id);
}

function handleToggleRole(participantId: string, currentRole: UserRole) {
    const newRole: UserRole = currentRole === 'voter' ? 'observer' : 'voter';
    setRole(participantId, newRole);
}

function handleVote(participantId: string, value: RequiredUserVote) {
    vote(participantId, value);
}

function handleClearVote(participantId: string) {
    clearVote(participantId);
}

const hasConnectedVoters = computed(() =>
    participants.value.some((p) => p.status.value === 'connected' && p.role.value === 'voter'),
);

const enhancedParticipants = computed(() =>
    participants.value.map((p) => ({
        ...p,
        statusColor: getStatusColor(p.status.value),
        name: (p.profile as any)?.name || `Mock User ${p.id.slice(0, 4)}`,
    })),
);
</script>

<template>
    <div class="flex flex-col gap-4">
        <span class="paragraph-md">Active Mock Participants ({{ enhancedParticipants.length }}/25)</span>

        <AddMockParticipant />
        <div
            v-if="enhancedParticipants.length === 0"
            class="w-full py-8 text-center bg-manaca-200 rounded paragraph-sm font-medium text-foreground-muted"
        >
            <p>No mock participants yet</p>
        </div>

        <template v-else>
            <!-- Bulk action: Vote All Random -->
            <PButton
                v-if="hasConnectedVoters"
                variant="outline"
                size="sm"
                class="w-full"
                :disabled="isVotingAll"
                @click="voteAllRandom"
            >
                {{ isVotingAll ? '🎲 Voting…' : '🎲 Vote All Random' }}
            </PButton>

            <div class="flex flex-col gap-2">
                <div
                    v-for="participant in enhancedParticipants"
                    :key="participant.id"
                    class="flex flex-col gap-2 w-full py-3 px-2 hover:bg-card-primary-hover border border-transparent hover:border-border-default rounded"
                >
                    <!-- Header row: icon + name + remove -->
                    <div class="flex justify-between">
                        <div class="flex grow gap-2">
                            <div class="relative">
                                <IconRobot />
                                <IconCircleFilled
                                    :class="cn('absolute size-3 -top-1 -right-1 ', participant.statusColor)"
                                />
                            </div>
                            <p class="grow">{{ participant.name }}</p>
                        </div>
                        <button
                            class="cursor-pointer"
                            @click="handleRemove(participant.id)"
                            title="Remove this participant"
                        >
                            <IconX class="size-4" />
                        </button>
                    </div>

                    <!-- Controls (only when connected) -->
                    <template v-if="participant.status.value === 'connected'">
                        <!-- Role toggle -->
                        <div class="flex items-center gap-2 ml-8">
                            <span class="text-xs text-foreground-muted">Role:</span>
                            <button
                                :class="
                                    cn(
                                        'text-xs px-2 py-0.5 rounded border cursor-pointer transition-colors',
                                        participant.role.value === 'voter'
                                            ? 'bg-primary text-primary-foreground border-primary'
                                            : 'bg-card-primary border-border-default hover:bg-card-primary-hover',
                                    )
                                "
                                @click="handleToggleRole(participant.id, participant.role.value)"
                            >
                                {{ participant.role.value === 'voter' ? 'Voter' : 'Observer' }}
                            </button>
                        </div>

                        <!-- Vote controls (only for voters) -->
                        <div v-if="participant.role.value === 'voter'" class="flex flex-col gap-1 ml-8">
                            <div class="flex items-center justify-between">
                                <span class="text-xs text-foreground-muted">Vote:</span>
                                <button
                                    v-if="participant.currentVote.value !== null"
                                    class="text-xs text-foreground-muted hover:underline cursor-pointer"
                                    @click="handleClearVote(participant.id)"
                                >
                                    Clear
                                </button>
                            </div>
                            <div class="flex flex-wrap gap-1">
                                <PButton
                                    size="xs"
                                    variant="outline"
                                    v-for="option in DEFAULT_ESTIMATION_OPTIONS"
                                    :key="String(option)"
                                    :class="
                                        cn(
                                            '',
                                            String(participant.currentVote.value) === String(option)
                                                ? 'bg-manaca-200'
                                                : '',
                                        )
                                    "
                                    @click="handleVote(participant.id, option)"
                                >
                                    {{ option }}
                                </PButton>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </template>
    </div>
</template>
